import {
	assemblyStatusCodes,
	CarrierAssemblyStatusTag,
} from "@/pages/mill/carriers/components/CarrierAssemblyStatusTag";
import { Carrier } from "@/pages/mill/carriers/models/Carrier";
import { ErrorModal } from "@dexteel/mesf-core";
import { Grid2, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Part } from "../../models/Part";
import { getCarriers } from "../../repositories/parts/CarrierRepository";

type useCarriersProps = {
	partId: number;
	onError: (error: Error) => void;
	onSuccess?: (data: Carrier[]) => void;
};

export const useCarriers = ({
	partId,
	onError,
	onSuccess,
}: useCarriersProps) => {
	return useQuery<Carrier[], Error>({
		queryKey: ["carriers", partId],
		queryFn: ({ signal }) => getCarriers({ partId }, signal),
		onError: onError,
		onSuccess: onSuccess,
	});
};

type Props = {
	part: Part | null;
};

export const SpecificCarrierData = ({ part }: Props) => {
	const [error, setError] = useState("");
	const [carrier, setCarrier] = useState<Carrier>();

	const { data: carriers } = useCarriers({
		partId: part?.PartId!,
		onError: (error) => setError(error.message),
		onSuccess: (data: Carrier[]) => {
			setCarrier(data[0]);
		},
	});

	const status = assemblyStatusCodes.find(
		(status) => status.Code === carrier?.CarrierStatusCode,
	);

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
					<TextField
						value={
							assemblyStatusCodes.find(
								(status) => status.Code === carrier?.CarrierStatusCode,
							)?.Name || ""
						}
						label="Assembly Status"
						variant="outlined"
						size="small"
						fullWidth
						InputProps={{
							startAdornment: (
								<span
									style={{
										width: "25px",
										height: "25px",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										marginRight: "8px",
										marginLeft: "8px",
									}}
								>
									<CarrierAssemblyStatusTag status={status} />
								</span>
							),
						}}
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={carrier?.SectionName || ""}
						label="Section"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={carrier?.StandName || ""}
						label="Stand"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={carrier?.Bait || ""}
						label="Bait"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 2 }}>
					<TextField
						value={carrier?.WeighUp || ""}
						label="Weigh Up"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
					<TextField
						value={carrier?.TopRollName || ""}
						label="Top Roll"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
					<TextField
						value={carrier?.BottomRollName || ""}
						label="Bottom Roll"
						variant="outlined"
						size="small"
						fullWidth
						disabled
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
